import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Checkbox, Text } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { centsToDollarsDisplay } from 'utils/currency';

const Row = styled.div`
  display: flex;
  margin-bottom: ${toRem(16)};
  gap: ${toRem(8)};
  align-items: center;
  width: 100%;
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  padding: ${toRem(16)};
  background-color: ${theme.color.neutral[50]};
  flex-flow: wrap;

  input {
    margin-right: ${toRem(8)};
  }
`;

interface IPillProps {
  appearance?: Appearance;
}

const Pill = styled.div<IPillProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(30)};
  padding: ${toRem(8)} ${toRem(12)};
  flex-basis: ${toRem(180)};
  flex-grow: 1;
  background-color: ${theme.color.white};
  text-transform: capitalize;

  label {
    font-weight: ${theme.font.weight.semiBold};
    font-size: ${toRem(11)};
  }

  &:hover {
    border: ${toRem(1)} solid ${theme.color.neutral[400]};
  }

  .react-loading-skeleton {
    opacity: 0.3;
    border-radius: ${toRem(8)};
  }

  ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success.light};
      border: ${toRem(1)} solid ${theme.color.success.medium};
      label,
      p {
        color: ${theme.color.success.dark};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.success.medium};
        background-image: ${`linear-gradient(90deg, ${theme.color.success.medium}, ${theme.color.neutral[25]}, ${theme.color.success.medium})`};
      }
    `}

  ${({ appearance }) =>
    appearance === Appearance.SECONDARY &&
    css`
      background-color: ${theme.color.secondary[25]};
      border: ${toRem(1)} solid ${theme.color.secondary[400]};
      label,
      p {
        color: ${theme.color.secondary[700]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.secondary[100]};
        background-image: ${`linear-gradient(90deg, ${theme.color.secondary[100]}, ${theme.color.neutral[25]}, ${theme.color.secondary[100]})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning.light};
      border: ${toRem(1)} solid ${theme.color.warning.medium};
      label,
      p {
        color: ${theme.color.warning.dark};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.warning.medium};
        background-image: ${`linear-gradient(90deg, ${theme.color.warning.medium}, ${theme.color.neutral[25]}, ${theme.color.warning.medium})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger.light};
      border: ${toRem(1)} solid ${theme.color.danger.medium};
      label,
      p {
        color: ${theme.color.danger.dark};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.danger.medium};
        background-image: ${`linear-gradient(90deg, ${theme.color.danger.medium}, ${theme.color.neutral[25]}, ${theme.color.danger.medium})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[100]};
      border: ${toRem(1)} solid ${theme.color.neutral[500]};
      label,
      p {
        color: ${theme.color.neutral[900]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.neutral[300]};
        background-image: ${`linear-gradient(90deg, ${theme.color.neutral[300]}, ${theme.color.neutral[25]}, ${theme.color.neutral[300]})`};
      }
    `}
`;

interface IStatus {
  label: string;
  value: number;
  count: number;
  appearance?: Appearance;
}

interface IStatusPillsProps {
  statuses: IStatus[];
  activeStatuses?: string[];
  onChange?(selectedStatus: string[]): void;
  isLoading?: boolean;
}

export const StatusPills = ({ statuses, activeStatuses, onChange, isLoading }: IStatusPillsProps) => {
  const onToggleCheckbox = (value: string, checked: boolean) => {
    let status;
    if (checked === true) {
      status = [...activeStatuses, value];
    } else {
      status = activeStatuses.filter((val) => val !== value);
    }
    onChange(status);
  };

  const getAppearance = (status: IStatus) => (!activeStatuses || activeStatuses?.includes(status.label) ? status.appearance : undefined);

  return (
    <Row data-cy="status-pills">
      {statuses.map((status) => (
        <Pill appearance={getAppearance(status)} key={status.label} data-cy={`status-pill-${status.label}`}>
          {isLoading ? (
            <>
              <Skeleton width={60} />
              <Skeleton width={70} />
            </>
          ) : (
            <>
              {activeStatuses ? (
                <Checkbox
                  id={`status-checkbox-${status.label}`}
                  name={`status-checkbox-${status.label}`}
                  data-cy={`status-checkbox-${status.label}`}
                  label={`${status.label} (${status.count})`}
                  size="small"
                  checked={activeStatuses.includes(status.label)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onToggleCheckbox(status.label, e.target.checked);
                  }}
                />
              ) : (
                <Text size="xxsmall" fontWeight="semibold">{`${status.label} (${status.count})`}</Text>
              )}
              <Text size="xxsmall" fontWeight="semibold">
                {centsToDollarsDisplay(status.value)}
              </Text>
            </>
          )}
        </Pill>
      ))}
    </Row>
  );
};
